/**
 * Lightweight FTUE toast (PEL-516).
 *
 * Anchorless corner notification for event signals that must survive navigation
 * (the toast fallback for event-gated steps). FTUE-scoped — intentionally not a
 * general app-wide toaster. Auto-dismisses after a delay; acknowledging or the
 * primary CTA both resolve the step. `aria-live=polite` announces it to AT.
 */

import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { ActiveFtueStep, FtueCta } from '../types'

const AUTO_DISMISS_MS = 8000

export function FtueToast({ active }: { active: ActiveFtueStep }) {
  const content = active.step.fallback?.content ?? active.step.content
  const { primaryCta } = content
  const activeRef = useRef(active)
  activeRef.current = active

  // Auto-dismiss (acknowledge) after a delay; reset when the step changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: timer resets only on step change; callbacks read via ref
  useEffect(() => {
    const timer = setTimeout(() => activeRef.current.onComplete(), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [active.step.id])

  const runCta = (cta: FtueCta) => {
    cta.onSelect?.()
    switch (cta.action) {
      case 'navigate':
        if (cta.href) activeRef.current.onCtaNavigate(cta.href)
        break
      case 'next':
        activeRef.current.onNext()
        break
      case 'skip':
        activeRef.current.onSkipAll()
        break
      default:
        activeRef.current.onComplete()
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: status live-region on a container, not an <output> form result
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[70] w-[320px] max-w-[calc(100vw-32px)] rounded-xl border border-border bg-background p-4 text-foreground shadow-xl motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:fade-in-0"
    >
      <div className="flex items-start justify-between gap-2">
        {content.title && <h3 className="text-sm font-semibold leading-tight">{content.title}</h3>}
        <button
          type="button"
          onClick={() => activeRef.current.onDismiss()}
          aria-label="Dismiss"
          className="-mr-1 -mt-1 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-1 text-sm leading-snug text-muted-foreground">{content.body}</p>
      {primaryCta && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={() => runCta(primaryCta)}>
            {primaryCta.label}
          </Button>
        </div>
      )}
    </div>
  )
}
